import React, { useState, useEffect, useCallback } from 'react';
import { Card, Spinner, Badge, Form } from 'react-bootstrap';
import { getStaffCreditAnalytics, getStudentCreditAnalytics } from '../../api/api';

const ROLE_COLOR = {
  faculty: '#6366f1', coe: '#ef4444', tenant_admin: '#0ea5e9',
  sme: '#f59e0b', qa: '#10b981', id: '#8b5cf6', pr: '#ec4899', media: '#14b8a6',
};

function BarCell({ value, max, color = '#6366f1' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 7, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
        ${value.toFixed(4)}
      </span>
    </div>
  );
}

function SummaryCards({ rows, valueKey = 'total_spent', label = 'Credits Spent' }) {
  const total = rows.reduce((s, r) => s + (r[valueKey] || 0), 0);
  const totalReqs = rows.reduce((s, r) => s + (r.request_count || 0), 0);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
      {[
        { label, value: `$${total.toFixed(4)}`, color: '#6366f1' },
        { label: 'Total AI Requests', value: totalReqs.toLocaleString(), color: '#0ea5e9' },
        { label: 'Users', value: rows.length, color: '#10b981' },
      ].map((s) => (
        <Card key={s.label} className="border-0 shadow-sm rounded-3 text-center p-3">
          <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
          <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>{s.label}</div>
        </Card>
      ))}
    </div>
  );
}

function StaffTable({ tenantSlug }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getStaffCreditAnalytics(tenantSlug)
      .then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [tenantSlug]);

  const maxSpent = Math.max(...rows.map((r) => r.total_spent), 0.001);
  const filtered = rows.filter((r) =>
    !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <SummaryCards rows={rows} />
      <div className="d-flex gap-2 mb-3">
        <Form.Control size="sm" placeholder="Search name or email…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 260 }} />
        {loading && <Spinner size="sm" animation="border" className="text-primary ms-1" />}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-hover table-sm align-middle small mb-0">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th style={{ minWidth: 160 }}>Credits Used</th>
              <th>Tokens In</th>
              <th>Tokens Out</th>
              <th>Requests</th>
              <th>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading ? (
              <tr><td colSpan={8} className="text-center text-muted py-4">No staff usage data found.</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={i}>
                <td className="fw-bold">{r.name || '—'}</td>
                <td className="text-muted" style={{ fontSize: 11 }}>{r.email}</td>
                <td>
                  <Badge style={{ background: ROLE_COLOR[r.role] || '#64748b', fontSize: 10 }}>
                    {r.role}
                  </Badge>
                </td>
                <td style={{ minWidth: 160 }}>
                  <BarCell value={r.total_spent} max={maxSpent} color={ROLE_COLOR[r.role] || '#6366f1'} />
                </td>
                <td>{(r.input_tokens || 0).toLocaleString()}</td>
                <td>{(r.output_tokens || 0).toLocaleString()}</td>
                <td>{(r.request_count || 0).toLocaleString()}</td>
                <td style={{ fontSize: 11, color: '#64748b' }}>
                  {r.last_active ? new Date(r.last_active).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StudentTable({ tenantSlug }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getStudentCreditAnalytics(tenantSlug)
      .then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [tenantSlug]);

  const maxSpent = Math.max(...rows.map((r) => r.total_spent), 0.001);
  const filtered = rows.filter((r) =>
    !search || (r.name || '').toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <SummaryCards rows={rows} label="Student Credits Used" />
      <div className="d-flex gap-2 mb-3">
        <Form.Control size="sm" placeholder="Search name or email…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 260 }} />
        {loading && <Spinner size="sm" animation="border" className="text-primary ms-1" />}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-hover table-sm align-middle small mb-0">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th style={{ minWidth: 160 }}>Credits Used</th>
              <th>Tokens In</th>
              <th>Tokens Out</th>
              <th>Sessions</th>
              <th>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading ? (
              <tr><td colSpan={7} className="text-center text-muted py-4">No student usage data found.</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={i}>
                <td className="fw-bold">{r.name || '—'}</td>
                <td className="text-muted" style={{ fontSize: 11 }}>{r.email}</td>
                <td style={{ minWidth: 160 }}>
                  <BarCell value={r.total_spent} max={maxSpent} color="#0ea5e9" />
                </td>
                <td>{(r.input_tokens || 0).toLocaleString()}</td>
                <td>{(r.output_tokens || 0).toLocaleString()}</td>
                <td>{(r.request_count || 0).toLocaleString()}</td>
                <td style={{ fontSize: 11, color: '#64748b' }}>
                  {r.last_active ? new Date(r.last_active).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TenantSubscriptionAnalytics({ tenantSlug }) {
  const [tab, setTab] = useState('staff');

  return (
    <div>
      <div className="mb-4">
        <h6 className="fw-bold text-dark mb-1">Subscription Analytics</h6>
        <p className="text-muted small mb-0">Credit usage breakdown by staff and students for this tenant.</p>
      </div>

      {/* Tab selector */}
      <div className="d-flex gap-1 mb-4 border-bottom pb-2">
        {[{ key: 'staff', label: '👥 Staff & Faculty' }, { key: 'students', label: '🎓 Students' }].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`btn btn-sm rounded-pill px-3 ${tab === key ? 'btn-primary' : 'btn-outline-secondary'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          {tab === 'staff' ? (
            <StaffTable tenantSlug={tenantSlug} />
          ) : (
            <StudentTable tenantSlug={tenantSlug} />
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
